import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Package, 
  Plus, 
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Wifi,
  Shield,
  Activity,
  TrendingUp,
  MapPin,
  Clock,
  BarChart3,
  RefreshCw,
  X,
  Upload,
  FileText,
  Download
} from 'lucide-react';

const Inventory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryOverview, setInventoryOverview] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const [formData, setFormData] = useState({
    item_type: 'firearm',
    make: '',
    model: '',
    serial_number: '',
    caliber: '',
    quantity: 1,
    location: '',
    status: 'in_stock',
    compliance_status: 'compliant',
    rfid_tag: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0
  });

  // Load inventory data on component mount and when filters change
  useEffect(() => {
    loadInventoryData();
    loadInventoryOverview();
  }, [searchTerm, filterStatus, filterCategory, pagination.page]);

  const loadInventoryData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      // Build query parameters
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        per_page: pagination.per_page.toString()
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterCategory !== 'all') params.append('category', filterCategory);
      
      const response = await fetch(`/inventory?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInventoryItems(data.items || []);
        setPagination(prev => ({
          ...prev,
          total: data.pagination?.total || 0,
          pages: data.pagination?.pages || 0
        }));
      } else {
        // Try to get error message from response
        let errorMsg = 'Failed to load inventory data';
        try {
          const errorData = await response.json();
          errorMsg = errorData.error || errorMsg;
        } catch {
          errorMsg = `Failed to load inventory: ${response.status} ${response.statusText}`;
        }
        setError(errorMsg);
        console.error('Inventory load error:', response.status, errorMsg);
      }
      
    } catch (err) {
      setError('Error loading inventory data');
      console.error('Error loading inventory:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadInventoryOverview = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('/inventory/overview', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInventoryOverview(data.overview || {});
      }
      
    } catch (err) {
      console.error('Error loading inventory overview:', err);
    }
  };

  const inventoryStats = {
    total: inventoryOverview.total_items || 0,
    active: inventoryOverview.active_items || 0,
    maintenance: inventoryOverview.maintenance_items || 0,
    inactive: inventoryOverview.inactive_items || 0,
    compliant: inventoryOverview.compliant_items || 0,
    iotOnline: inventoryOverview.online_devices || 0
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'maintenance':
        return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case 'inactive':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Package className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-400';
      case 'maintenance':
        return 'text-yellow-400';
      case 'inactive':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'maintenance':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'inactive':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getComplianceColor = (compliance) => {
    switch (compliance) {
      case 'compliant':
        return 'text-green-400';
      case 'warning':
        return 'text-yellow-400';
      case 'non-compliant':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getIoTStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'text-green-400';
      case 'offline':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  // Since filtering is now done on the server side, we just use the inventoryItems directly
  const filteredItems = inventoryItems;

  const handleAddItem = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('/inventory/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      // Handle non-JSON responses (like 401, 500 errors)
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        // If response is not JSON, create error object
        const text = await response.text();
        data = { error: text || `Server error: ${response.status} ${response.statusText}` };
      }

      if (response.ok) {
        setSubmitSuccess('Inventory item added successfully!');
        // Reset form
        setFormData({
          item_type: 'firearm',
          make: '',
          model: '',
          serial_number: '',
          caliber: '',
          quantity: 1,
          location: '',
          status: 'in_stock',
          compliance_status: 'compliant',
          rfid_tag: ''
        });
        // Reload inventory
        setTimeout(() => {
          setShowAddModal(false);
          loadInventoryData();
          loadInventoryOverview();
          setSubmitSuccess(null);
        }, 1500);
      } else {
        setSubmitError(data.error || 'Failed to add inventory item');
      }
    } catch (err) {
      setSubmitError('Network error. Please try again.');
      console.error('Error adding inventory item:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'quantity' ? parseInt(value) || 1 : value
    }));
    if (submitError) setSubmitError(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      parseCSVFile(file);
    } else {
      alert('Please select a CSV file');
    }
  };

  const parseCSVFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.trim().split('\n');
      
      if (lines.length < 2) {
        alert('CSV file must have at least a header row and one data row');
        return;
      }
      
      // Parse header row
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
      
      // Parse data rows
      const items = lines.slice(1).map((line, index) => {
        const values = line.split(',').map(v => v.trim());
        const item = {};
        headers.forEach((header, i) => {
          item[header] = values[i] || '';
        });
        
        // Map common CSV column names to our API fields
        return {
          item_type: item.item_type || item.type || item.category || 'firearm',
          make: item.make || item.manufacturer || '',
          model: item.model || '',
          serial_number: item.serial_number || item.serial || '',
          caliber: item.caliber || item.cal || '',
          quantity: parseInt(item.quantity || item.qty || '1') || 1,
          location: item.location || item.storage_location || '',
          status: item.status || 'in_stock',
          compliance_status: item.compliance_status || item.compliance || 'compliant',
          rfid_tag: item.rfid_tag || item.rfid || ''
        };
      }).filter(item => item.item_type); // Filter out empty rows
      
      setCsvPreview(items);
    };
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    if (!csvPreview || csvPreview.length === 0) {
      alert('Please upload a CSV file first');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setBulkImportResult(null);

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('/inventory/bulk-import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ items: csvPreview })
      });

      const data = await response.json();

      if (response.ok) {
        setBulkImportResult({
          imported: data.imported_count,
          total: data.total_items,
          errors: data.errors || []
        });
        // Reload inventory after a delay
        setTimeout(() => {
          loadInventoryData();
          loadInventoryOverview();
        }, 2000);
      } else {
        setSubmitError(data.error || 'Failed to import inventory items');
      }
    } catch (err) {
      setSubmitError('Network error. Please try again.');
      console.error('Error importing inventory:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadCSVTemplate = () => {
    const headers = ['item_type', 'make', 'model', 'serial_number', 'caliber', 'quantity', 'location', 'status', 'compliance_status', 'rfid_tag'];
    const example = ['firearm', 'Glock', 'G19', 'ABC123', '9mm', '1', 'Warehouse A', 'in_stock', 'compliant', 'RFID001'];
    const csv = [headers.join(','), example.join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'inventory_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Inventory Management</h1>
          <p className="text-gray-400 mt-1">Manage firearm inventory with IoT tracking and compliance monitoring</p>
        </div>
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => {
              loadInventoryData();
              loadInventoryOverview();
            }}
            disabled={isLoading}
            className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition-colors flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button 
            onClick={() => setShowBulkUpload(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center mr-3"
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Upload
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <XCircle className="w-5 h-5 text-red-400" />
            <div>
              <h3 className="text-red-400 font-medium">Error Loading Inventory</h3>
              <p className="text-red-300 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Total Items</p>
              <p className="text-2xl font-bold text-white">{inventoryStats.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-blue-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-blue-400">
              <TrendingUp className="w-4 h-4 mr-1" />
              <span>All tracked</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Active Items</p>
              <p className="text-2xl font-bold text-white">{inventoryStats.active}</p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-green-400">
              <CheckCircle className="w-4 h-4 mr-1" />
              <span>Ready for use</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">IoT Online</p>
              <p className="text-2xl font-bold text-white">{inventoryStats.iotOnline}</p>
            </div>
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Wifi className="w-6 h-6 text-purple-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-purple-400">
              <Wifi className="w-4 h-4 mr-1" />
              <span>Real-time tracking</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Compliant</p>
              <p className="text-2xl font-bold text-white">{inventoryStats.compliant}</p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-400" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm text-green-400">
              <Shield className="w-4 h-4 mr-1" />
              <span>Regulatory compliant</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card"
      >
        <div className="flex items-center space-x-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by name, serial, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
          </div>
          
          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="maintenance">Maintenance</option>
            <option value="inactive">Inactive</option>
          </select>
          
          {/* Category Filter */}
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="all">All Categories</option>
            <option value="Handgun">Handgun</option>
            <option value="Rifle">Rifle</option>
            <option value="Shotgun">Shotgun</option>
          </select>
        </div>
      </motion.div>

      {/* Inventory List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card"
      >
        <h2 className="text-xl font-semibold text-white mb-6">Inventory Items</h2>
        
        <div className="space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
            </div>
          ) : filteredItems.length > 0 ? (
            filteredItems.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="p-4 bg-gray-700 rounded-lg border border-gray-600 hover:border-gray-500 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Package className="w-6 h-6 text-blue-400" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-1">
                      <h3 className="font-medium text-white">
                        {item.make && item.model ? `${item.make} ${item.model}` : item.item_type || 'Inventory Item'}
                      </h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadgeColor(item.status || 'in_stock')}`}>
                        {item.status || 'in_stock'}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getComplianceColor(item.compliance_status || 'compliant') === 'text-green-400' ? 'bg-green-500/20 text-green-400 border-green-500/30' : getComplianceColor(item.compliance_status || 'compliant') === 'text-yellow-400' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'}`}>
                        {item.compliance_status || 'compliant'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-400 mt-2">
                      <div>
                        <span className="text-gray-500">Type:</span> {item.item_type || 'N/A'}
                      </div>
                      <div>
                        <span className="text-gray-500">Serial:</span> {item.serial_number || 'N/A'}
                      </div>
                      <div>
                        <span className="text-gray-500">Caliber:</span> {item.caliber || 'N/A'}
                      </div>
                      <div>
                        <span className="text-gray-500">Location:</span> {item.location || 'N/A'}
                      </div>
                    </div>
                    
                    {item.quantity && item.quantity > 1 && (
                      <div className="text-sm text-gray-400 mt-2">
                        <span className="text-gray-500">Quantity:</span> {item.quantity}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-blue-400 hover:bg-blue-500/20 rounded transition-colors" title="View Details">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-green-400 hover:bg-green-500/20 rounded transition-colors" title="Edit">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors" title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-400">
              <Package className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <p className="text-lg font-medium">No inventory items found</p>
              <p className="text-sm">Try adjusting your search or filters</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!isLoading && pagination.pages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-600">
            <div className="text-sm text-gray-400">
              Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} items
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page <= 1}
                className="px-3 py-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                Previous
              </button>
              <span className="px-3 py-2 text-gray-400">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.pages, prev.page + 1) }))}
                disabled={pagination.page >= pagination.pages}
                className="px-3 py-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="card"
      >
        <h2 className="text-xl font-semibold text-white mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="p-4 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-left">
            <div className="flex items-center space-x-3">
              <BarChart3 className="w-6 h-6 text-white" />
              <div>
                <div className="font-medium text-white">Generate Report</div>
                <div className="text-sm text-blue-200">Create inventory summary</div>
              </div>
            </div>
          </button>
          
          <button className="p-4 bg-green-600 hover:bg-green-700 rounded-lg transition-colors text-left">
            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6 text-white" />
              <div>
                <div className="font-medium text-white">Compliance Check</div>
                <div className="text-sm text-green-200">Verify all items</div>
              </div>
            </div>
          </button>
          
          <button className="p-4 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors text-left">
            <div className="flex items-center space-x-3">
              <Wifi className="w-6 h-6 text-white" />
              <div>
                <div className="font-medium text-white">IoT Status</div>
                <div className="text-sm text-purple-200">Check device health</div>
              </div>
            </div>
          </button>
        </div>
      </motion.div>

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-slate-800 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Add New Inventory Item</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSubmitError(null);
                  setSubmitSuccess(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {submitSuccess && (
              <div className="mb-4 p-3 bg-green-900/20 border border-green-500/50 rounded-lg">
                <p className="text-green-400 text-sm">{submitSuccess}</p>
              </div>
            )}

            {submitError && (
              <div className="mb-4 p-3 bg-red-900/20 border border-red-500/50 rounded-lg">
                <p className="text-red-400 text-sm">{submitError}</p>
              </div>
            )}

            <form onSubmit={handleAddItem} className="space-y-4">
              {/* Item Type */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Item Type <span className="text-red-400">*</span>
                </label>
                <select
                  name="item_type"
                  value={formData.item_type}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  required
                >
                  <option value="firearm">Firearm</option>
                  <option value="ammunition">Ammunition</option>
                  <option value="accessory">Accessory</option>
                </select>
              </div>

              {/* Make and Model */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Make
                  </label>
                  <input
                    type="text"
                    name="make"
                    value={formData.make}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    placeholder="e.g., Glock, Smith & Wesson"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Model
                  </label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    placeholder="e.g., G19, M&P9"
                  />
                </div>
              </div>

              {/* Serial Number and Caliber */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Serial Number
                  </label>
                  <input
                    type="text"
                    name="serial_number"
                    value={formData.serial_number}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    placeholder="Enter serial number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Caliber
                  </label>
                  <input
                    type="text"
                    name="caliber"
                    value={formData.caliber}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    placeholder="e.g., 9mm, .45 ACP"
                  />
                </div>
              </div>

              {/* Quantity and Location */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Quantity
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    min="1"
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    placeholder="Storage location"
                  />
                </div>
              </div>

              {/* Status and Compliance */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  >
                    <option value="in_stock">In Stock</option>
                    <option value="sold">Sold</option>
                    <option value="transferred">Transferred</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Compliance Status
                  </label>
                  <select
                    name="compliance_status"
                    value={formData.compliance_status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  >
                    <option value="compliant">Compliant</option>
                    <option value="pending">Pending</option>
                    <option value="non-compliant">Non-Compliant</option>
                  </select>
                </div>
              </div>

              {/* RFID Tag */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  RFID Tag
                </label>
                <input
                  type="text"
                  name="rfid_tag"
                  value={formData.rfid_tag}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                  placeholder="Optional RFID tag identifier"
                />
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setSubmitError(null);
                    setSubmitSuccess(null);
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Item
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-slate-800 rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Bulk Upload Inventory</h2>
              <button
                onClick={() => {
                  setShowBulkUpload(false);
                  setCsvFile(null);
                  setCsvPreview([]);
                  setBulkImportResult(null);
                  setSubmitError(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {submitError && (
              <div className="mb-4 p-3 bg-red-900/20 border border-red-500/50 rounded-lg">
                <p className="text-red-400 text-sm">{submitError}</p>
              </div>
            )}

            {bulkImportResult && (
              <div className="mb-4 p-4 bg-green-900/20 border border-green-500/50 rounded-lg">
                <h3 className="text-green-400 font-medium mb-2">Import Complete!</h3>
                <p className="text-green-300 text-sm mb-2">
                  Successfully imported {bulkImportResult.imported} of {bulkImportResult.total} items
                </p>
                {bulkImportResult.errors && bulkImportResult.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-yellow-400 text-sm font-medium mb-1">Errors ({bulkImportResult.errors.length}):</p>
                    <ul className="text-yellow-300 text-xs list-disc list-inside max-h-32 overflow-y-auto">
                      {bulkImportResult.errors.slice(0, 10).map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                      {bulkImportResult.errors.length > 10 && (
                        <li>... and {bulkImportResult.errors.length - 10} more errors</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-4">
              {/* Instructions */}
              <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-4">
                <h3 className="text-blue-400 font-medium mb-2 flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  CSV Format Instructions
                </h3>
                <p className="text-blue-300 text-sm mb-2">
                  Upload a CSV file with the following columns (required fields marked with *):
                </p>
                <ul className="text-blue-200 text-xs list-disc list-inside space-y-1 mb-3">
                  <li><strong>item_type*</strong> - firearm, ammunition, or accessory</li>
                  <li><strong>make</strong> - Manufacturer name (e.g., Glock, Smith & Wesson)</li>
                  <li><strong>model</strong> - Model name (e.g., G19, M&P9)</li>
                  <li><strong>serial_number</strong> - Serial number</li>
                  <li><strong>caliber</strong> - Caliber (e.g., 9mm, .45 ACP)</li>
                  <li><strong>quantity</strong> - Quantity (default: 1)</li>
                  <li><strong>location</strong> - Storage location</li>
                  <li><strong>status</strong> - in_stock, sold, transferred, lost (default: in_stock)</li>
                  <li><strong>compliance_status</strong> - compliant, pending, non-compliant (default: compliant)</li>
                  <li><strong>rfid_tag</strong> - Optional RFID tag</li>
                </ul>
                <button
                  type="button"
                  onClick={downloadCSVTemplate}
                  className="text-blue-400 hover:text-blue-300 text-sm flex items-center"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download CSV Template
                </button>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Select CSV File
                </label>
                <div
                  className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-blue-500 transition-colors"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('border-blue-500', 'bg-blue-500/10');
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-500/10');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-500/10');
                    const file = e.dataTransfer.files[0];
                    if (file && file.type === 'text/csv') {
                      setCsvFile(file);
                      parseCSVFile(file);
                    } else {
                      alert('Please drop a CSV file');
                    }
                  }}
                >
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="csvFileInput"
                  />
                  <label
                    htmlFor="csvFileInput"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="w-12 h-12 text-gray-400 mb-2" />
                    <p className="text-gray-300 mb-1">
                      {csvFile ? csvFile.name : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-gray-500 text-sm">CSV files only</p>
                  </label>
                </div>
              </div>

              {/* Preview */}
              {csvPreview.length > 0 && (
                <div>
                  <h3 className="text-white font-medium mb-2">
                    Preview ({csvPreview.length} items)
                  </h3>
                  <div className="bg-gray-900 rounded-lg overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-800 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-gray-300">Type</th>
                          <th className="px-3 py-2 text-left text-gray-300">Make</th>
                          <th className="px-3 py-2 text-left text-gray-300">Model</th>
                          <th className="px-3 py-2 text-left text-gray-300">Serial</th>
                          <th className="px-3 py-2 text-left text-gray-300">Caliber</th>
                          <th className="px-3 py-2 text-left text-gray-300">Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.slice(0, 10).map((item, idx) => (
                          <tr key={idx} className="border-t border-gray-700">
                            <td className="px-3 py-2 text-gray-400">{item.item_type}</td>
                            <td className="px-3 py-2 text-gray-400">{item.make || '-'}</td>
                            <td className="px-3 py-2 text-gray-400">{item.model || '-'}</td>
                            <td className="px-3 py-2 text-gray-400">{item.serial_number || '-'}</td>
                            <td className="px-3 py-2 text-gray-400">{item.caliber || '-'}</td>
                            <td className="px-3 py-2 text-gray-400">{item.quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {csvPreview.length > 10 && (
                      <p className="text-gray-500 text-xs p-2 text-center">
                        Showing first 10 of {csvPreview.length} items
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkUpload(false);
                    setCsvFile(null);
                    setCsvPreview([]);
                    setBulkImportResult(null);
                    setSubmitError(null);
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleBulkImport}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center"
                  disabled={isSubmitting || csvPreview.length === 0}
                >
                  {isSubmitting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import {csvPreview.length} Items
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
